/**
 * Dart Match Sheet Generator - JavaScript
 * Manages state, live preview, and PDF generation
 * Single source of truth: config object
 */

(function() {
  'use strict';

  // Game type configurations
  const GAME_TYPES = {
    cricket: { label: 'Cricket', category: 'other' },
    '301': { label: '301', category: '01', hasOptions: true },
    '501': { label: '501', category: '01', hasOptions: true },
    '701': { label: '701', category: '01', hasOptions: true },
    score: { label: 'Plus haut score', category: 'score' },
    libre: { label: 'Libre', category: 'other' }
  };

  const FINISH_TYPES = ['Libre', 'Double', 'Master'];
  const FROZEN_OPTIONS = ['Oui', 'Non'];

  // Initial config state
  const config = {
    phase: 'poules',
    format: 'individuel',
    koType: '',
    level: 'pro',
    customLevel: '',
    // New manches/BO structure
    manchesMode: '3-joues', // '3-joues', '5-joues', 'libre-joues', 'bo3', 'bo5', 'bo-libre'
    customManches: '',
    // Game rounds: array of { gameType, finish (for 01), frozen (for 01), darts (for score) }
    rounds: [
      { gameType: 'cricket', finish: 'Libre', frozen: 'Non', darts: '3' },
      { gameType: '501', finish: 'Double', frozen: 'Non', darts: '3' },
      { gameType: '701', finish: 'Master', frozen: 'Non', darts: '3' }
    ],
    lastRoundChoice: false,
    lastRoundGame2: '',
    logo: null,
    duplicatePages: false
  };

  const dom = {};
  const state = { rendering: false };

  /**
   * Initialize the application
   */
  function init() {
    cacheDOM();
    bindEvents();
    // Initialize manches select value
    dom.manchesSelect.value = config.manchesMode;
    updateRoundsContainer();
    render();
  }

  /**
   * Cache DOM references
   */
  function cacheDOM() {
    dom.container = document.querySelector('.dart-match-generator');
    dom.configPanel = dom.container.querySelector('.config-panel');
    dom.previewContainer = document.getElementById('preview-container');
    dom.downloadBtn = document.getElementById('download-pdf');
    dom.phaseSel = document.getElementById('phase');
    dom.formatSel = document.getElementById('format');
    dom.finalesOptions = document.getElementById('finales-options');
    dom.koTypeSel = document.getElementById('kotype');
    dom.levelSel = document.getElementById('level');
    dom.customLevel = document.getElementById('custom-level');
    dom.manchesSelect = document.getElementById('num-manches-select');
    dom.customManches = document.getElementById('custom-num-manches');
    dom.gameTypesContainer = document.getElementById('game-types-container');
    dom.lastRoundChoice = document.getElementById('last-round-choice');
    dom.lastRoundChoiceSection = document.getElementById('last-round-choice-section');
    dom.logoUpload = document.getElementById('logo-upload');
    dom.logoPreview = document.getElementById('logo-preview');
    dom.logoPreviewContainer = document.getElementById('logo-preview-container');
    dom.removeLogo = document.getElementById('remove-logo');
    dom.duplicatePages = document.getElementById('duplicate-pages');
  }

  /**
   * Bind event listeners
   */
  function bindEvents() {
    // Phase changes
    dom.phaseSel.addEventListener('change', (e) => {
      config.phase = e.target.value;
      updateUIVisibility();
      render();
    });

    // Format changes
    dom.formatSel.addEventListener('change', (e) => {
      config.format = e.target.value;
      render();
    });

    // Finales settings
    dom.koTypeSel.addEventListener('change', (e) => {
      config.koType = e.target.value;
      render();
    });

    dom.levelSel.addEventListener('change', (e) => {
      config.level = e.target.value;
      if (e.target.value === 'libre') {
        dom.customLevel.style.display = 'block';
      } else {
        dom.customLevel.style.display = 'none';
      }
      render();
    });

    dom.customLevel.addEventListener('input', (e) => {
      config.customLevel = e.target.value;
      render();
    });

    // Manches mode select
    dom.manchesSelect.addEventListener('change', (e) => {
      config.manchesMode = e.target.value;
      const isCustom = e.target.value.includes('libre');
      dom.customManches.style.display = isCustom ? 'block' : 'none';
      updateRoundsContainer();
      render();
    });

    dom.customManches.addEventListener('input', (e) => {
      const val = parseInt(e.target.value);
      if (!isNaN(val) && val >= 1 && val <= 9) {
        config.customManches = val.toString();
        updateRoundsContainer();
        render();
      }
    });

    // Last round choice
    dom.lastRoundChoice.addEventListener('change', (e) => {
      config.lastRoundChoice = e.target.checked;
      updateRoundsContainer();
      render();
    });

    // Logo upload
    dom.logoUpload.addEventListener('change', (e) => {
      if (e.target.files && e.target.files[0]) {
        const reader = new FileReader();
        reader.onload = (event) => {
          config.logo = event.target.result;
          dom.logoPreview.src = event.target.result;
          dom.logoPreviewContainer.style.display = 'block';
          render();
        };
        reader.readAsDataURL(e.target.files[0]);
      }
    });

    dom.removeLogo.addEventListener('click', (e) => {
      e.preventDefault();
      config.logo = null;
      dom.logoUpload.value = '';
      dom.logoPreviewContainer.style.display = 'none';
      render();
    });

    // Duplicate pages
    dom.duplicatePages.addEventListener('change', (e) => {
      config.duplicatePages = e.target.checked;
      render();
    });

    // Download PDF
    dom.downloadBtn.addEventListener('click', downloadPDF);
  }

  /**
   * Update UI visibility based on phase
   */
  function updateUIVisibility() {
    if (config.phase === 'finales') {
      dom.finalesOptions.style.display = 'block';
    } else {
      dom.finalesOptions.style.display = 'none';
    }
  }

  /**
   * Get the number of rounds based on current mode
   */
  function getNumRounds() {
    const mode = config.manchesMode;
    if (mode === '3-joues') return 3;
    if (mode === '5-joues') return 5;
    if (mode === 'bo3') return 3;
    if (mode === 'bo5') return 5;
    if (mode === 'libre-joues' || mode === 'bo-libre') {
      const val = parseInt(config.customManches);
      if (!isNaN(val) && val >= 1 && val <= 9) return val;
      return 3; // default
    }
    return 3;
  }

  /**
   * Check if current mode is "Best Of"
   */
  function isBO() {
    return config.manchesMode.startsWith('bo');
  }

  /**
   * Calculate wins needed for BO
   */
  function getWinsNeeded() {
    const n = getNumRounds();
    return Math.ceil(n / 2);
  }

  /**
   * Update rounds container with game selectors
   */
  function updateRoundsContainer() {
    const numRounds = getNumRounds();
    dom.gameTypesContainer.innerHTML = '';

    // Resize rounds array if needed
    while (config.rounds.length < numRounds) {
      config.rounds.push({
        gameType: 'cricket',
        finish: 'Double',
        frozen: 'Non',
        darts: '3'
      });
    }
    if (config.rounds.length > numRounds) {
      config.rounds = config.rounds.slice(0, numRounds);
    }

    const label = document.createElement('label');
    label.textContent = 'Types de jeu par manche:';
    label.style.fontWeight = 'bold';
    label.style.marginBottom = '10px';
    label.style.display = 'block';
    dom.gameTypesContainer.appendChild(label);

    for (let i = 0; i < numRounds; i++) {
      const roundDiv = document.createElement('div');
      roundDiv.className = 'round-config';
      roundDiv.style.marginBottom = '15px';
      roundDiv.style.padding = '10px';
      roundDiv.style.backgroundColor = '#f9f9f9';
      roundDiv.style.borderRadius = '4px';
      roundDiv.style.border = '1px solid #ddd';

      const roundLabel = document.createElement('label');
      roundLabel.style.display = 'block';
      roundLabel.style.marginBottom = '8px';
      roundLabel.style.fontWeight = '600';
      roundLabel.style.fontSize = '12px';

      const isLastRound = i === numRounds - 1;
      if (isLastRound && config.lastRoundChoice) {
        roundLabel.textContent = `Manche ${i + 1} (Choix 1):`;
      } else {
        roundLabel.textContent = `Manche ${i + 1}:`;
      }
      roundDiv.appendChild(roundLabel);

      // Game type selector
      const gameSelect = document.createElement('select');
      gameSelect.style.width = '100%';
      gameSelect.style.padding = '6px';
      gameSelect.style.fontSize = '12px';
      gameSelect.style.marginBottom = '8px';
      gameSelect.style.boxSizing = 'border-box';

      Object.entries(GAME_TYPES).forEach(([key, info]) => {
        const option = document.createElement('option');
        option.value = key;
        option.textContent = info.label;
        gameSelect.appendChild(option);
      });

      gameSelect.value = config.rounds[i].gameType;
      gameSelect.addEventListener('change', (e) => {
        config.rounds[i].gameType = e.target.value;
        updateRoundsContainer();
        render();
      });
      roundDiv.appendChild(gameSelect);

      // Show additional options based on game type
      const gameType = config.rounds[i].gameType;
      if (gameType === '301' || gameType === '501' || gameType === '701') {
        // Finish type selector
        const finishLabel = document.createElement('label');
        finishLabel.textContent = 'Type de sortie:';
        finishLabel.style.display = 'block';
        finishLabel.style.marginBottom = '4px';
        finishLabel.style.fontSize = '11px';
        finishLabel.style.fontWeight = '500';
        roundDiv.appendChild(finishLabel);

        const finishSelect = document.createElement('select');
        finishSelect.style.width = '100%';
        finishSelect.style.padding = '6px';
        finishSelect.style.fontSize = '12px';
        finishSelect.style.marginBottom = '8px';
        finishSelect.style.boxSizing = 'border-box';

        FINISH_TYPES.forEach(type => {
          const option = document.createElement('option');
          option.value = type;
          option.textContent = type;
          finishSelect.appendChild(option);
        });

        finishSelect.value = config.rounds[i].finish || 'Double';
        finishSelect.addEventListener('change', (e) => {
          config.rounds[i].finish = e.target.value;
          render();
        });
        roundDiv.appendChild(finishSelect);

        // Frozen selector
        const frozenLabel = document.createElement('label');
        frozenLabel.textContent = 'Frozen:';
        frozenLabel.style.display = 'block';
        frozenLabel.style.marginBottom = '4px';
        frozenLabel.style.fontSize = '11px';
        frozenLabel.style.fontWeight = '500';
        roundDiv.appendChild(frozenLabel);

        const frozenSelect = document.createElement('select');
        frozenSelect.style.width = '100%';
        frozenSelect.style.padding = '6px';
        frozenSelect.style.fontSize = '12px';
        frozenSelect.style.marginBottom = '8px';
        frozenSelect.style.boxSizing = 'border-box';

        FROZEN_OPTIONS.forEach(type => {
          const option = document.createElement('option');
          option.value = type;
          option.textContent = type;
          frozenSelect.appendChild(option);
        });

        frozenSelect.value = config.rounds[i].frozen || 'Non';
        frozenSelect.addEventListener('change', (e) => {
          config.rounds[i].frozen = e.target.value;
          render();
        });
        roundDiv.appendChild(frozenSelect);
      } else if (gameType === 'score') {
        // Darts selector for "Plus haut score"
        const dartsLabel = document.createElement('label');
        dartsLabel.textContent = 'Fléchettes jouées:';
        dartsLabel.style.display = 'block';
        dartsLabel.style.marginBottom = '4px';
        dartsLabel.style.fontSize = '11px';
        dartsLabel.style.fontWeight = '500';
        roundDiv.appendChild(dartsLabel);

        const dartsSelect = document.createElement('select');
        dartsSelect.style.width = '100%';
        dartsSelect.style.padding = '6px';
        dartsSelect.style.fontSize = '12px';
        dartsSelect.style.marginBottom = '8px';
        dartsSelect.style.boxSizing = 'border-box';

        ['3', '6', '9'].forEach(num => {
          const option = document.createElement('option');
          option.value = num;
          option.textContent = num;
          dartsSelect.appendChild(option);
        });

        // Libre option
        const libreOption = document.createElement('option');
        libreOption.value = 'libre';
        libreOption.textContent = 'Libre';
        dartsSelect.appendChild(libreOption);

        dartsSelect.value = config.rounds[i].darts || '3';
        dartsSelect.addEventListener('change', (e) => {
          config.rounds[i].darts = e.target.value;
          updateRoundsContainer();
          render();
        });
        roundDiv.appendChild(dartsSelect);

        // If libre is selected, show custom input
        if (config.rounds[i].darts === 'libre') {
          const customDartsInput = document.createElement('input');
          customDartsInput.type = 'number';
          customDartsInput.min = '1';
          customDartsInput.max = '30';
          customDartsInput.placeholder = 'Nombre de fléchettes (1-30)';
          customDartsInput.style.width = '100%';
          customDartsInput.style.padding = '6px';
          customDartsInput.style.fontSize = '12px';
          customDartsInput.style.boxSizing = 'border-box';
          customDartsInput.value = config.rounds[i].customDarts || '';
          customDartsInput.addEventListener('input', (e) => {
            const val = parseInt(e.target.value);
            if (!isNaN(val) && val >= 1 && val <= 30) {
              config.rounds[i].customDarts = val.toString();
              render();
            }
          });
          roundDiv.appendChild(customDartsInput);
        }
      }

      dom.gameTypesContainer.appendChild(roundDiv);
    }

    // Show/hide last round choice option
    if (numRounds > 1) {
      dom.lastRoundChoiceSection.style.display = 'block';
    } else {
      dom.lastRoundChoiceSection.style.display = 'none';
      config.lastRoundChoice = false;
      dom.lastRoundChoice.checked = false;
    }

    // Add "Choix 2" selector if last round choice is enabled
    if (config.lastRoundChoice && numRounds > 1) {
      const choix2Label = document.createElement('label');
      choix2Label.textContent = 'Choix 2:';
      choix2Label.style.display = 'block';
      choix2Label.style.marginBottom = '4px';
      choix2Label.style.fontSize = '11px';
      choix2Label.style.fontWeight = '500';
      choix2Label.style.marginTop = '10px';

      const choix2Select = document.createElement('select');
      choix2Select.style.width = '100%';
      choix2Select.style.padding = '6px';
      choix2Select.style.fontSize = '12px';
      choix2Select.style.boxSizing = 'border-box';

      Object.entries(GAME_TYPES).forEach(([key, info]) => {
        const option = document.createElement('option');
        option.value = key;
        option.textContent = info.label;
        choix2Select.appendChild(option);
      });

      choix2Select.value = config.lastRoundGame2 || '';
      choix2Select.addEventListener('change', (e) => {
        config.lastRoundGame2 = e.target.value;
        render();
      });

      dom.gameTypesContainer.appendChild(choix2Label);
      dom.gameTypesContainer.appendChild(choix2Select);
    }
  }

  /**
   * Get game title for display in column header
   */
  function getGameColumnTitle(roundIdx) {
    const round = config.rounds[roundIdx];
    if (!round) return '';

    const numRounds = getNumRounds();
    const isLastRound = roundIdx === numRounds - 1;

    function singleTitle(r) {
      let title = '';
      if (r.gameType === 'cricket') title = 'CRICKET';
      else if (r.gameType === '301') title = `301 SORTIE ${r.finish.toUpperCase()}`;
      else if (r.gameType === '501') title = `501 SORTIE ${r.finish.toUpperCase()}`;
      else if (r.gameType === '701') title = `701 SORTIE ${r.finish.toUpperCase()}`;
      else if (r.gameType === 'score') title = 'PLUS HAUT SCORE';
      else if (r.gameType === 'libre') title = 'LIBRE';
      
      // Add Frozen label if applicable
      if ((['301', '501', '701'].includes(r.gameType)) && r.frozen === 'Oui') {
        title += ' (Frozen)';
      }
      
      return title;
    }

    if (isLastRound && config.lastRoundChoice && config.lastRoundGame2) {
      const game1 = singleTitle(round);
      const game2Round = {
        gameType: config.lastRoundGame2,
        finish: config.rounds[roundIdx].finish,
        frozen: config.rounds[roundIdx].frozen,
        darts: config.rounds[roundIdx].darts
      };
      const game2 = singleTitle(game2Round);
      if (game1 && game2) return `${game1} OU ${game2}`;
    }

    return singleTitle(round);
  }

  /**
   * Generate dynamic subtitle based on configuration
   */
  function generateExplanatoryText() {
    const numRounds = getNumRounds();

    if (config.phase === 'finales') {
      // For finales, use same structure as poules but with different rules
      // Base common phrase
      let text = 'Le plus proche du centre de la cible choisit sa position sur la feuille de match.';

      // Check if any round is a 01 type (301/501/701) with Frozen = OUI
      const has01Games = config.rounds.some(r => ['301', '501', '701'].includes(r.gameType));
      const allFrozen01 = has01Games && config.rounds
        .filter(r => ['301', '501', '701'].includes(r.gameType))
        .every(r => r.frozen === 'Oui');

      // Frozen phrase (if applicable)
      if (has01Games) {
        if (allFrozen01) {
          text += ' Les 01 se jouent en Teams 2/2 Frozen.';
        } else {
          text += ' Les 01 se jouent en Teams 2/2.';
        }
      }

      // Mode phrase
      if (isBO()) {
        const winsNeeded = getWinsNeeded();
        text += ` Dès qu'une équipe atteint ${winsNeeded} manche${winsNeeded > 1 ? 's' : ''}, le match est terminé.`;
      } else {
        text += ' Toutes les manches doivent être jouées.';
      }

      // Last round phrase
      text += ' Pour la dernière manche, le plus proche du centre décide du type de jeu ou démarrer.';

      return text;
    }

    // Base common phrase for poules
    let text = 'Le plus proche du centre de la cible choisit sa position sur la feuille de match.';

    // Check if any round is a 01 type (301/501/701) with Frozen = OUI
    const has01Games = config.rounds.some(r => ['301', '501', '701'].includes(r.gameType));
    const allFrozen01 = has01Games && config.rounds
      .filter(r => ['301', '501', '701'].includes(r.gameType))
      .every(r => r.frozen === 'Oui');

    // Doublettes + Frozen phrase (if applicable)
    if (config.format === 'doublettes' && has01Games) {
      if (allFrozen01) {
        text += ' Les 01 se jouent en Teams 2/2 Frozen.';
      } else {
        text += ' Les 01 se jouent en Teams 2/2.';
      }
    }

    // Mode phrase
    if (isBO()) {
      const winsNeeded = getWinsNeeded();
      text += ` Dès qu'une équipe atteint ${winsNeeded} manche${winsNeeded > 1 ? 's' : ''}, le match est terminé.`;
    } else {
      text += ' Toutes les manches doivent être jouées.';
    }

    // Last round phrase
    text += ' Pour la dernière manche, le plus proche du centre décide du type de jeu ou démarrer.';

    return text;
  }

  /**
   * Get finals info line (level - KO type)
   */
  function getFinalsInfoLine() {
    if (config.phase !== 'finales') return '';
    const level = config.level === 'libre' ? config.customLevel : config.level.toUpperCase();
    const koType = config.koType ? (config.koType === 'double' ? 'Double KO' : 'Simple KO') : '';
    return `${level}${koType ? ' - ' + koType : ''}`;
  }

  /**
   * Render the entire preview
   */
  function render() {
    if (state.rendering) return;
    state.rendering = true;

    try {
      const sheetHTML = generateMatchSheetHTML();

      if (config.duplicatePages) {
        const html = `
          <div class="match-sheet-duplicate">
            <div class="match-sheet-half" style="border-bottom: 1px dashed #999;">
              ${sheetHTML}
            </div>
            <div class="match-sheet-half">
              ${sheetHTML}
            </div>
          </div>
        `;
        dom.previewContainer.innerHTML = html;
      } else {
        dom.previewContainer.innerHTML = sheetHTML;
      }
    } finally {
      state.rendering = false;
    }
  }

  /**
   * Get team color for a specific round (column)
   */
  function getTeamColor(teamIdx, roundIdx, numRounds) {
    // Last round never colored
    if (roundIdx === numRounds - 1) {
      return 'white';
    }

    // Team 1: Light Pink/White alternation (50% lighter)
    if (teamIdx === 0) {
      return roundIdx % 2 === 0 ? '#f4a0b0' : 'white';
    }

    // Team 2: White/Light Turquoise alternation (50% lighter)
    if (teamIdx === 1) {
      return roundIdx % 2 === 0 ? 'white' : '#8addd4';
    }

    return 'white';
  }

  /**
   * Generate match sheet HTML
   */
  function generateMatchSheetHTML() {
    const numRounds = getNumRounds();
    const userLogo = config.logo
      ? `<img src="${config.logo}" style="max-width: 100px; max-height: 60px; object-fit: contain;" alt="Logo" />`
      : '';
    const tournoisLogo = '<img src="https://cdn.shopify.com/s/files/1/0725/4124/1608/files/logo_tournois-apps.webp?v=1768837092" style="max-width: 100px; max-height: 60px; object-fit: contain;" alt="Tournois Apps Logo" />';

    let html = `
      <div class="sheet-header" style="position: relative; display: flex; align-items: flex-start; gap: 20px;">
        <!-- Logo on top left -->
        <div style="flex: 0 0 auto; min-width: 100px;">
          ${userLogo}
        </div>

        <!-- Title in center -->
        <div style="flex: 1; text-align: center;">
          <div class="sheet-title">
            FEUILLE DE MATCH / PHASE ${config.phase === 'finales' ? 'FINALES' : 'DE POULES'} / ${config.format.toUpperCase()}
          </div>
        </div>

        <!-- Tournois Apps Logo on top right -->
        <div style="flex: 0 0 auto; min-width: 100px;">
          ${tournoisLogo}
        </div>
      </div>

      ${config.phase === 'finales' ? `<div style="font-size: 12px; font-weight: bold; text-align: center; margin-bottom: 10px;">${getFinalsInfoLine()}</div>` : ''}

      <div class="sheet-subtitle" style="text-align: center; font-size: 10px; line-height: 1.4; margin-bottom: 12px;">
        ${generateExplanatoryText()}
      </div>

      <table class="sheet-table" style="width:100%; border-collapse: collapse;">
        <thead>
          <tr style="background: #f9f9f9;">
            <th style="width: 30%; text-align: left; padding: 6px; border: 0.5px solid #333;">Numéro et nom d'équipe:</th>
    `;

    // Column headers for each round
    for (let i = 0; i < numRounds; i++) {
      const gameTitle = getGameColumnTitle(i);
      html += `
            <th style="text-align: center; padding: 6px; border: 0.5px solid #333; font-size: 8px; line-height:1.1; word-wrap: break-word;">${gameTitle}</th>
      `;
    }

    html += `
            <th style="text-align: center; padding: 6px; border: 0.5px solid #333;">TOTAL</th>
          </tr>
        </thead>
        <tbody>
    `;

    // Team 1 row
    html += `
          <tr>
            <td style="background: #f4a0b0; padding: 8px; border: 0.5px solid #333; text-align: left; font-weight: bold;">
            </td>
    `;

    for (let i = 0; i < numRounds; i++) {
      const bgColor = getTeamColor(0, i, numRounds);
      html += `
            <td style="background: ${bgColor}; height: 50px; border: 0.5px solid #333;"></td>
      `;
    }

    html += `
            <td style="background: #f0f0f0; height: 50px; border: 0.5px solid #333;"></td>
          </tr>
    `;

    // Team 2 row
    html += `
          <tr>
            <td style="background: #8addd4; padding: 8px; border: 0.5px solid #333; text-align: left; font-weight: bold;">
            </td>
    `;

    for (let i = 0; i < numRounds; i++) {
      const bgColor = getTeamColor(1, i, numRounds);
      html += `
            <td style="background: ${bgColor}; height: 50px; border: 0.5px solid #333;"></td>
      `;
    }

    html += `
            <td style="background: #f0f0f0; height: 50px; border: 0.5px solid #333;"></td>
          </tr>
        </tbody>
      </table>

      <div class="signature-section" style="margin-top: 30px; border: 0.5px solid #333; padding: 20px; min-height: 80px;">
        SIGNATURE DES DEUX ÉQUIPES :
      </div>

      <div class="footer-info" style="margin-top: 15px; font-size: 10px; color: #666; display: flex; justify-content: space-between;">
        <div style="flex: 1;">
          <div>
            Besoin d'aide pour organiser un tournoi ? Découvrez les logiciels <strong>Tournois°</strong> : <a href="https://tournois-apps.com">tournois-apps.com</a>
          </div>
          <div style="margin-top: 5px;">
            Découvrez les tournois près de chez vous : <a href="https://calendrier.tournois-apps.com/">https://calendrier.tournois-apps.com/</a>
          </div>
        </div>
        <div style="flex: 0 0 auto; margin-left: 10px;">
          <img src="https://cdn.shopify.com/s/files/1/0725/4124/1608/files/qr-code_1.png?v=1769700432" alt="QR Code" style="width: 60px; height: 60px; object-fit: contain;" />
        </div>
      </div>

      <div style="margin-top: 15px; font-size: 8px; color: #333; text-align: left; font-weight: normal;">
        IPNS ne pas jeter sur la voie publique
      </div>
    `;

    return html;
  }

  /**
   * Validate configuration
   */
  function isConfigValid() {
    // Check number of manches
    const numRounds = getNumRounds();
    if (numRounds < 1 || numRounds > 9) {
      return false;
    }

    // Check all rounds have valid game types
    for (let i = 0; i < config.rounds.length; i++) {
      const round = config.rounds[i];
      if (!round || !GAME_TYPES[round.gameType]) {
        return false;
      }

      // Check 01 types have finish type
      if (['301', '501', '701'].includes(round.gameType)) {
        if (!round.finish || !FINISH_TYPES.includes(round.finish)) {
          return false;
        }
        if (!round.frozen || !FROZEN_OPTIONS.includes(round.frozen)) {
          return false;
        }
      }

      // Check score type has valid darts
      if (round.gameType === 'score') {
        if (round.darts === 'libre') {
          const customVal = parseInt(round.customDarts);
          if (isNaN(customVal) || customVal < 1 || customVal > 30) {
            return false;
          }
        } else if (!['3', '6', '9'].includes(round.darts)) {
          return false;
        }
      }
    }

    // Check last round choice if enabled
    if (config.lastRoundChoice) {
      if (!config.lastRoundGame2 || !GAME_TYPES[config.lastRoundGame2]) {
        return false;
      }
    }

    return true;
  }

  /**
   * Download PDF
   */
  async function downloadPDF() {
    if (!isConfigValid()) {
      alert('Configuration invalide. Veuillez vérifier tous les paramètres.');
      return;
    }

    const element = dom.previewContainer;
    if (!element) {
      console.error('Preview container not found');
      return;
    }

    const button = dom.downloadBtn;
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Génération en cours...';

    try {
      if (typeof html2canvas === 'undefined') {
        alert('Erreur: html2canvas non chargé');
        return;
      }

      let contentElement = element;

      if (config.duplicatePages) {
        contentElement.style.width = '210mm';
        contentElement.style.height = '297mm';
        contentElement.style.margin = '0';
      }

      const canvas = await html2canvas(contentElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        allowTaint: true,
        width: config.duplicatePages ? 794 : undefined,
        height: config.duplicatePages ? 1191 : undefined
      });

      const imgData = canvas.toDataURL('image/png');

      let imgWidth = 210;
      let imgHeight = 297;

      if (!config.duplicatePages) {
        imgHeight = (canvas.height * imgWidth) / canvas.width;
      }

      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF('p', 'mm', 'a4');

      if (config.duplicatePages) {
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      } else {
        let position = 0;
        const pageHeight = 297;

        while (position < imgHeight) {
          const remainingHeight = imgHeight - position;
          const heightOnPage = Math.min(remainingHeight, pageHeight);

          if (position > 0) {
            pdf.addPage();
          }

          pdf.addImage(imgData, 'PNG', 0, position === 0 ? 0 : -position, imgWidth, imgHeight);
          position += pageHeight;
        }
      }

      pdf.save('feuille-match-flechettes.pdf');

    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF');
    } finally {
      button.disabled = false;
      button.textContent = originalText;
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
